import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { environment } from '../../../environments/environment';
import { AccountControllerService, AuthControllerService } from 'api-auth';
import { take } from 'rxjs/internal/operators/take';

interface LoginData {
	id?: string;
	clientId?: string;
	role?: string;
	scopes?: string[];
	identifier?: string;
	password?: string;
	tokenType?: string;
	accessToken?: string;
	accessTokenValidity?: number;
	refreshToken?: string;
}

@Injectable({
	providedIn: 'root',
})
export class AuthenticationService {
	public loginData: BehaviorSubject<any> = new BehaviorSubject(null);
	// private refreshTokenTimeout: any;
	private sessionExpiredTimeout: any;

	constructor(
		private apiAuth: AuthControllerService,
		private apiAuthAccount: AccountControllerService // private utils: UtilsService
	) {
		this.initializeLoginService();
	}

	private initializeLoginService() {
		if (this.getStoredData('LOCAL', 'loginData')) {
			// this is requested feature, clients should remain logged in after first login
				this.setLoginData(this.getStoredData('LOCAL', 'loginData'));
        this.setLoginData(this.getStoredData('SESSION', 'loginData'));
				setTimeout(() => {
          this.timerManagement('start', this.sessionExpiredTimeout);
        }, 100);
		} else if (this.getStoredData('SESSION', 'loginData')) {
			this.setLoginData(this.getStoredData('SESSION', 'loginData'));
			setTimeout(() => {
        this.timerManagement('start', this.sessionExpiredTimeout);
      }, 100);
		}
	}

	public setLoginData(data: LoginData) {
		if (data && Object.keys(data).length > 0) {
			let loginData: LoginData = this.loginData.getValue() || {
				id: '',
				clientId: '',
				role: '',
				scopes: '',
				identifier: '',
				password: '',
				tokenType: '',
				accessToken: '',
				accessTokenValidity: '',
				refreshToken: '',
			};
			Object.keys(data).forEach(key => {
				(loginData as any)[key] = (data as any)[key];
			});
			this.loginData.next(loginData);
		}
	}

	public userLogin(loginData: LoginData) {
		this.setLoginData({ ...loginData });
		this.getUserProfile();
		this.timerManagement('start', this.sessionExpiredTimeout);
		localStorage.removeItem('logoutCause');
	}

	public userLogout(location?: string) {
		this.removeStoredData('LOCAL', 'loginData');
		this.removeStoredData('SESSION', 'loginData');
		this.loginData.next(null);
		this.timerManagement('stop', this.sessionExpiredTimeout);
		if (!window.location.toString().includes('/account/login')) {
			window.location.reload();
		}
		localStorage.setItem('logoutCause', location || 'safe');
	}

	private timerManagement(action: 'start' | 'stop', timerRef: any) {
		if (timerRef) {
			clearTimeout(timerRef);
		}
		if (action === 'stop') {
			return;
		}
		const REFRESH_BEFORE_EXPIRED_MS = 60000;
		const sessionStatus = this.loginData.getValue();
		if (sessionStatus && sessionStatus.accessTokenValidity) {
			let diff = new Date(sessionStatus.accessTokenValidity).getTime() - new Date().getTime() - REFRESH_BEFORE_EXPIRED_MS;
			if (diff > 0) {
				timerRef = setTimeout(() => this.timerComplete(), diff);
			} else {
				this.refreshToken();
			}
		}
	}

	private timerComplete() {
		const sessionStatus = this.loginData.getValue();
		if (sessionStatus && sessionStatus.accessTokenValidity) {
			const diff = new Date(sessionStatus.accessTokenValidity).getTime() - new Date().getTime();
			if (diff < 0) {
				this.userLogout('Access token timed out');
				// window.location.reload();
				// this.utils.showToaster('MESSAGE.INFO', 'LOGIN.SESSION_EXPIRED', 'info');
			} else {
				this.refreshToken();
			}
		}
	}

	private getStoredData(storage: 'LOCAL' | 'SESSION', itemName: string, key?: string) {
		const storeRaw = storage === 'LOCAL' ? localStorage.getItem(itemName) : sessionStorage.getItem(itemName);
		if (storeRaw) {
			const store = JSON.parse(storeRaw);
			const data = key ? store[key] : store;
			return data;
		}
		return null;
	}
	private storeData(storage: 'LOCAL' | 'SESSION', itemName: string, data: any) {
		storage === 'LOCAL' ? localStorage.setItem(itemName, JSON.stringify(data)) : sessionStorage.setItem(itemName, JSON.stringify(data));
	}
	private removeStoredData(storage: 'LOCAL' | 'SESSION', itemName: string) {
		storage === 'LOCAL' ? localStorage.removeItem(itemName) : sessionStorage.removeItem(itemName);
	}

	private refreshToken(refreshToken?: string) {
		const sessionStatus = this.loginData.getValue();

		if (refreshToken || (sessionStatus && sessionStatus.refreshToken)) {
			this.apiAuth
				.refreshToken({ refreshToken: refreshToken || sessionStatus.refreshToken })
				.pipe(take(1))
				.subscribe({
					next: response => {
						this.setLoginData(response);
						this.storeData(sessionStatus.role === 'CLIENT' ? 'LOCAL' : 'SESSION', 'loginData', this.loginData.getValue());
						this.timerManagement('start', this.sessionExpiredTimeout);
					},
					error: error => {
						this.userLogout('Refresh token error: ' + error && error.message ? error.message : error);
						// window.location.reload();
						// this.utils.showToaster('MESSAGE.INFO', 'LOGIN.SESSION_EXPIRED', 'info');
					},
				});
		}
	}

	private getUserProfile() {
		this.apiAuthAccount
			.getAccountDetails()
			.pipe(take(1))
			.subscribe({
				next: response => {
					this.setLoginData(response);
					// this is requested feature, clients should remain logged in after first login
					this.storeData(response.role === 'CLIENT' ? 'LOCAL' : 'SESSION', 'loginData', this.loginData.getValue());
				},
				error: error => {
					this.userLogout('No profile');
				},
			});
	}
}
